import React, { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/services/apiClient";
import { TrashIcon, SpinnerIcon } from "@/components/icons";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

interface AccessRecord {
  accountId: string;
  email: string | null;
  name: string | null;
  role: string;
  granted_at: string | null;
}

interface ClientSharingPanelProps {
  clientId: string;
}

export const ClientSharingPanel: React.FC<ClientSharingPanelProps> = ({
  clientId,
}) => {
  const [accessList, setAccessList] = useState<AccessRecord[]>([]);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGranting, setIsGranting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AccessRecord | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const loadAccess = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/api/client/${clientId}/access`);
      setAccessList(res.access || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to load access list";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsGranting(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await apiClient.post(`/api/client/${clientId}/access`, {
        email: email.trim(),
      });
      if (res.success) {
        setSuccessMsg(`Access granted to ${email.trim()}`);
        setEmail("");
        await loadAccess();
      } else {
        setError(res.error || "Failed to grant access");
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to grant access";
      setError(message);
    } finally {
      setIsGranting(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;

    setIsRevoking(true);
    try {
      await apiClient.delete(
        `/api/client/${clientId}/access/${revokeTarget.accountId}`
      );
      setRevokeTarget(null);
      await loadAccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to revoke access";
      setError(message);
    } finally {
      setIsRevoking(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-gray-900">Shared Access</h3>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 rounded-sm px-3 py-2">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="text-sm text-green-600 bg-green-50 rounded-sm px-3 py-2">
          {successMsg}
        </div>
      )}

      <form onSubmit={handleGrant} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={isGranting || !email.trim()}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGranting ? (
            <SpinnerIcon className="w-4 h-4" />
          ) : (
            "Grant Access"
          )}
        </button>
      </form>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <SpinnerIcon className="w-4 h-4" />
          Loading...
        </div>
      ) : accessList.length === 0 ? (
        <p className="text-sm text-gray-500">
          No shared access. Only you can see this client.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {accessList.map((record) => (
            <li
              key={record.accountId}
              className="flex items-center justify-between py-2"
            >
              <div>
                <div className="text-sm text-gray-900">
                  {record.name || record.email || "Unknown"}
                </div>
                {record.name && record.email && (
                  <div className="text-xs text-gray-500">{record.email}</div>
                )}
              </div>
              <button
                onClick={() => setRevokeTarget(record)}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Revoke access"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        title="Revoke Access"
        message={`Remove access for ${revokeTarget?.name || revokeTarget?.email || "this account"}? They will no longer be able to view this client.`}
        confirmText="Revoke"
        variant="danger"
        isLoading={isRevoking}
      />
    </div>
  );
};
